import { useMutation } from "@apollo/client";
import { Dialog } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { MUTATION_LOGOUT } from "api/mutations/account";
import LogoDark from "components/Image/Logo/Dark";
import Loading from "components/Loading";
import { APP_URLS } from "settings";
import { useUserStore } from "store/User";
import { cn } from "utils/cn";

// Matches NavTab in render's @render/ui-components (page-header.tsx).
const navTabClassName = (active: boolean) =>
  cn(
    "inline-flex items-center rounded-lg px-3.5 py-2 text-sm font-semibold no-underline transition-colors duration-150",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
    active
      ? "bg-secondary text-primary hover:text-primary"
      : "text-brand-copy-light hover:bg-muted hover:text-foreground",
  );

const isPathActive = (pathname: string, prefixes: string[]) =>
  prefixes.some((prefix) =>
    prefix === APP_URLS.DASHBOARD
      ? pathname === prefix
      : pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

const HeaderAuthenticated = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState<boolean>(false);

  const user = useUserStore((state) => state.user);
  const { pathname } = useLocation();

  const navigation = [
    {
      name: "Dashboard",
      href: APP_URLS.DASHBOARD,
      match: [APP_URLS.DASHBOARD],
      roles: "BROKER",
    },
    {
      name: "Cases",
      href: APP_URLS.CASE_MANAGEMENT,
      // The case tracker has no nav item while we test it (temporary), so
      // highlight Cases when it is open.
      match: [APP_URLS.CASE_MANAGEMENT, APP_URLS.CASE_TRACKER],
      roles: "*",
    },
    {
      name: "Resources",
      href: APP_URLS.RESOURCES,
      match: [APP_URLS.RESOURCES],
      roles: "*",
    },
    {
      name: "Account",
      href: APP_URLS.ACCOUNT.ACCOUNT_PROFILE,
      match: [APP_URLS.ACCOUNT.ACCOUNT_PROFILE],
      roles: "*",
    },
  ];

  const [logout, { error: mutationError, data: mutationData }] =
    useMutation(MUTATION_LOGOUT);

  useEffect(() => {
    if (mutationData || mutationError) {
      setTimeout(() => (window.location.href = APP_URLS.DASHBOARD), 1000);
    }
  }, [mutationData, mutationError]);

  const handleLogout = () => {
    setLoggingOut(true);
    setMobileMenuOpen(false);
    logout();
  };

  if (!user) {
    return <></>;
  }

  return (
    <header className="bg-white">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
        aria-label="Global"
      >
        <Link to={APP_URLS.DASHBOARD} className="-m-1.5 p-1.5">
          <span className="sr-only">Abound</span>
          <LogoDark />
        </Link>
        <div className="flex lg:hidden">
          {loggingOut ? (
            <Loading small />
          ) : (
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="hidden lg:flex lg:items-center lg:gap-x-2">
          {loggingOut ? (
            <Loading small />
          ) : (
            <>
              {navigation.map((item) => {
                if (
                  item.roles === "*" ||
                  item.roles.split(",").includes(user.role)
                ) {
                  const active = isPathActive(pathname, item.match);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      aria-current={active ? "page" : undefined}
                      className={navTabClassName(active)}
                    >
                      {item.name}
                    </Link>
                  );
                }
                return null;
              })}
              <button className={navTabClassName(false)} onClick={handleLogout}>
                <span>Logout</span>
              </button>
            </>
          )}
        </div>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link
              to={APP_URLS.DASHBOARD}
              onClick={() => setMobileMenuOpen(false)}
              className="-m-1.5 p-1.5"
            >
              <span className="sr-only">Abound</span>
              <LogoDark />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="divide-y divide-gray-300">
              <div className="space-y-2 py-6 text-center">
                {navigation
                  .filter(
                    (item) =>
                      item.roles === "*" ||
                      item.roles.split(",").includes(user.role),
                  )
                  .map((item) => {
                    const active = isPathActive(pathname, item.match);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          navTabClassName(active),
                          "-mx-3 flex justify-center px-3 text-lg",
                        )}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
              </div>
              <div className="py-6 text-center">
                <button
                  className="-mx-3 rounded-lg px-3 py-2.5 text-lg font-medium leading-7 text-brand-copy-primary"
                  onClick={handleLogout}
                >
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
};

export default HeaderAuthenticated;
